import React from 'react';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {Box, Checkbox, CircularProgress, FormControlLabel, Typography} from '@mui/material';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import {useGetRecipientsPreviewQuery} from '../../../../api/services/formAssignmentsService';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';

/* eslint-disable react/prop-types */

const RecipientsPreviewDialog = ({
    open,
    onClose,
    groupFilters,
    strictRecipientsFiltering = false,
    audience,
    accessLevel,
    recipientType = null,
    externalEmails = [],
}) => {
    // Backend FormAudienceType has no "enquiry" — enquiry forms use parents (2). Preview must not send 3.
    const previewAudienceType = React.useMemo(() => {
        const n = Number(audience);
        return n === FORM_AUDIENCE_TYPE.enquiries
            ? FORM_AUDIENCE_TYPE.parentsForChildCompletion
            : n;
    }, [audience]);

    const requestBody = React.useMemo(
        () =>
            open && groupFilters?.length > 0 && audience != null
                ? {
                      groupFilters: groupFilters.map((g) => ({
                          filterId: g.id,
                          excluded: !!g.excluded,
                      })),
                      strictRecipientsFiltering: !!strictRecipientsFiltering,
                      audienceType: previewAudienceType,
                      ...(accessLevel != null && { accessLevel }),
                      ...(recipientType != null && recipientType !== 0 && { recipientType }),
                  }
                : null,
        [open, groupFilters, strictRecipientsFiltering, audience, previewAudienceType, accessLevel, recipientType]
    );

    const {data, isLoading, isError, error} = useGetRecipientsPreviewQuery(requestBody, {
        skip: !requestBody,
    });

    const items = data?.items ?? [];
    const audienceNum = Number(audience);

    const normalizedExternalEmails = (externalEmails ?? [])
        .filter((x) => x && !x.isDeleted)
        .map((x) => (typeof x === 'string' ? x : x.email))
        .filter(Boolean);

    const externalEmailsText = normalizedExternalEmails.join(', ');

    const baseItems =
        audienceNum === FORM_AUDIENCE_TYPE.externalUsers && externalEmailsText
            ? items.map((item) => ({
                  ...item,
                  assigneeEmail: item.assigneeEmail || externalEmailsText,
              }))
            : items;

    const totalCount = data?.totalCount ?? baseItems.length;
    const willReceiveCount = baseItems.filter(
        (item) => item.status == null || item.status === ''
    ).length;
    const [hideExcludedRecipients, setHideExcludedRecipients] = React.useState(false);
    const displayedItems = hideExcludedRecipients
        ? baseItems.filter((item) => !item.status)
        : baseItems;
    const showStudent =
        audienceNum === FORM_AUDIENCE_TYPE.students ||
        audienceNum === FORM_AUDIENCE_TYPE.enquiries ||
        audienceNum === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
        audienceNum === FORM_AUDIENCE_TYPE.previousSchool;

    const columns = React.useMemo(() => {
        const cols = [];
        const isExternalAudience = audienceNum === FORM_AUDIENCE_TYPE.externalUsers;

        if (showStudent) {
            cols.push({
                accessorKey: 'studentName',
                header: 'Student name',
                size: 140,
                Cell: ({row}) =>
                    row.original.studentName ?? row.original.name ?? row.original.personId ?? '—',
            });
        }
        cols.push(
            {
                accessorKey: 'assigneeName',
                header: isExternalAudience ? 'Student name' : 'Assignee name',
                size: 140,
                grow: true,
                Cell: ({row}) => {
                    const {assigneeName, name, personId} = row.original;

                    if (isExternalAudience) {
                        return assigneeName ?? name ?? personId ?? '—';
                    }

                    // For non-external audiences, do NOT fall back to student/name.
                    // If there's no explicit assignee, leave the cell empty.
                    return assigneeName ?? '';
                },
            },
            {
                accessorKey: 'assigneeEmail',
                header: 'Assignee email',
                size: 180,
                Cell: ({row}) => {
                    const email = row.original.assigneeEmail ?? '—';

                    return (
                        <Typography
                            variant="body2"
                            title={email}
                            sx={{
                                display: 'block',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {email}
                        </Typography>
                    );
                },
            },
            {
                accessorKey: 'status',
                header: '',
                size: 140,
                Cell: ({row}) => {
                    const status = row.original.status;
                    if (status == null || status === '') {
                        return null;
                    }
                    return (
                        <Typography component="span" variant="body2" color="warning.dark">
                            {status}
                        </Typography>
                    );
                },
            }
        );
        return cols;
    }, [showStudent, audienceNum]);

    return (
        <AdmicityDialog
            title="Recipients preview"
            open={open}
            handleClose={onClose}
            maxWidth="md"
            actions={[{label: 'Close', onClick: onClose}]}
        >
            <Box sx={{minHeight: 200}}>
                {isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4,
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                )}
                {isError && (
                    <Typography color="error" sx={{py: 2}}>
                        {error?.data?.message ?? 'Failed to load recipients preview.'}
                    </Typography>
                )}
                {!isLoading && !isError && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {totalCount} recipient{totalCount !== 1 ? 's' : ''} match the selected
                                groups
                                {totalCount > 0 && (
                                    <> ({willReceiveCount} will receive the form)</>
                                )}
                                .
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={hideExcludedRecipients}
                                        onChange={(e) => setHideExcludedRecipients(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        Hide recipients who will not get the form
                                    </Typography>
                                }
                            />
                        </Box>
                        <AdmicityTableV2
                            emptyStateMinHeight={120}
                            noRowsOverlay={{text: 'No recipients match the selected groups.'}}
                            columns={columns}
                            data={displayedItems}
                            tableProps={{
                                layoutMode: 'grid',
                                enableSorting: false,
                                enableFilters: false,
                                enableHiding: false,
                                enableFullScreenToggle: false,
                                enableColumnResizing: false,
                                enableBottomToolbar: false,
                                enableTopToolbar: false,
                                enablePagination: displayedItems.length > 10,
                                enableStickyHeader: true,
                                muiTablePaperProps: {
                                    variant: 'outlined',
                                    elevation: 0,
                                    sx: {
                                        display: 'block',
                                        flexGrow: 0,
                                        width: '100%',
                                        height: 'auto',
                                    },
                                },
                                muiTableContainerProps: {
                                    sx: {
                                        flex: 'unset',
                                        minHeight: 200,
                                        maxHeight: 360,
                                        overflow: 'auto',
                                    },
                                },
                                muiTableBodyRowProps: ({row}) => ({
                                    sx: row.original.status
                                        ? { opacity: 0.6, backgroundColor: 'grey.100' }
                                        : {},
                                }),
                            }}
                        />
                    </>
                )}
            </Box>
        </AdmicityDialog>
    );
};

export default RecipientsPreviewDialog;
